import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, FileSpreadsheet, Loader2, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import Portal from './UI/Portal';
import { staffAPI } from '../services/api';
import { CountBadge } from './UI/CountBadge';
import { InfoBadge } from './UI/InfoBadge';
import toast from '../utils/toast';
import { getTodayInSingaporeTimezone } from '../utils/dateUtils';

const DEFAULT_FIELD_IDS = [
  'staff_id', 'full_name', 'employment_classification', 'employment_status',
  'designation', 'department_name', 'employment_start_date', 'work_email',
  'nationality', 'employment_pass_type',
];

const SENSITIVE_FIELD_IDS = new Set([
  'nric_fin', 'passport_number', 'wp_number', 'personal_email', 'personal_phone',
  'residential_address', 'postal_code', 'date_of_birth', 'race', 'religion',
  'marital_status', 'passport_expiry', 'pass_expiry',
]);

export default function EmployeeExportModal({ isOpen, onClose, activeFilters }) {
  const [fieldGroups, setFieldGroups] = useState([]);
  const [selectedFields, setSelectedFields] = useState(new Set(DEFAULT_FIELD_IDS));
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [fetchingFields, setFetchingFields] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setFetchingFields(true);
    setError(null);
    staffAPI.getExportFields()
      .then(res => {
        if (cancelled) return;
        setFieldGroups(res.data.groups || []);
        setFetchingFields(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load export fields.');
        setFetchingFields(false);
      });
    return () => { cancelled = true; };
  }, [isOpen]);

  const toggleField = useCallback((fieldId) => {
    setSelectedFields(prev => {
      const next = new Set(prev);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  }, []);

  const toggleGroup = useCallback((group) => {
    const groupFieldIds = group.fields.map(f => f.id);
    const allSelected = groupFieldIds.every(id => selectedFields.has(id));
    setSelectedFields(prev => {
      const next = new Set(prev);
      groupFieldIds.forEach(id => { allSelected ? next.delete(id) : next.add(id); });
      return next;
    });
  }, [selectedFields]);

  const toggleCollapseGroup = useCallback((groupName) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    const allIds = fieldGroups.flatMap(g => g.fields.map(f => f.id));
    setSelectedFields(new Set(allIds));
  }, [fieldGroups]);

  const selectNone = useCallback(() => {
    setSelectedFields(new Set());
  }, []);

  const resetDefaults = useCallback(() => {
    setSelectedFields(new Set(DEFAULT_FIELD_IDS));
  }, []);

  const handleExport = useCallback(async () => {
    if (selectedFields.size === 0) {
      toast.error('Select at least one field to export.');
      return;
    }
    setLoading(true);
    try {
      const params = {
        fields: Array.from(selectedFields).join(','),
      };
      if (activeFilters.search) params.search = activeFilters.search;
      if (activeFilters.department) params.department = activeFilters.department;
      if (activeFilters.employment_classification) {
        params.employment_classification = activeFilters.employment_classification;
      }
      if (activeFilters.employment_status) params.employment_status = activeFilters.employment_status;
      if (activeFilters.include_inactive) params.include_inactive = 'true';

      const response = await staffAPI.exportStaff(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.headers['content-disposition']
        ?.match(/filename="?([^"]+)"?/)?.[1]
        || `employees_export_${getTodayInSingaporeTimezone()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Employee data exported successfully.');
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedFields, activeFilters, onClose]);

  if (!isOpen) return null;

  const activeFilterLabels = [];
  if (activeFilters.search) activeFilterLabels.push(`Search: "${activeFilters.search}"`);
  if (activeFilters.department) activeFilterLabels.push(`Dept: ${activeFilters.department}`);
  if (activeFilters.employment_classification === 'outsourced') {
    activeFilterLabels.push('Workforce: Outsourced');
  }
  if (activeFilters.employment_status) {
    const statusMap = {
      active: 'Active',
      inactive: 'Inactive',
      probation: 'Probation',
      notice: 'Notice',
    };
    activeFilterLabels.push(`Status: ${statusMap[activeFilters.employment_status] || activeFilters.employment_status}`);
  }

  const totalFieldCount = fieldGroups.reduce((sum, g) => sum + g.fields.length, 0);

  return (
    <Portal>
      <div className="modal-overlay">
        <div className="modal-container modal-container-md">
          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-50 rounded-xl">
                <FileSpreadsheet className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="modal-title">Export Employees</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Select fields to include in the CSV export
                </p>
              </div>
            </div>
            <button onClick={onClose} className="modal-close-admin">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="modal-content">
            {/* Active filters summary */}
            {activeFilterLabels.length > 0 && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Filters applied to export</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Only employees matching your current filters will be exported:
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {activeFilterLabels.map((label, i) => (
                        <InfoBadge key={i} color="amber" label={label} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Toolbar */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  Fields
                </span>
                <CountBadge count={selectedFields.size} theme="admin" size="sm" />
                <span className="text-xs text-gray-400">
                  of {totalFieldCount}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-xs text-red-600 hover:text-red-700 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded px-1.5 py-0.5"
                >
                  All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={selectNone}
                  className="text-xs text-gray-500 hover:text-gray-700 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded px-1.5 py-0.5"
                >
                  None
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={resetDefaults}
                  className="text-xs text-gray-500 hover:text-gray-700 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded px-1.5 py-0.5"
                >
                  Defaults
                </button>
              </div>
            </div>

            {/* Field groups */}
            {fetchingFields ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-red-500 animate-spin" />
                <span className="ml-3 text-sm text-gray-500">Loading export fields...</span>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600">{error}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto overscroll-y-contain pr-1">
                {fieldGroups.map((group) => {
                  const groupFieldIds = group.fields.map(f => f.id);
                  const selectedInGroup = groupFieldIds.filter(id => selectedFields.has(id)).length;
                  const allSelected = selectedInGroup === groupFieldIds.length;
                  const someSelected = selectedInGroup > 0 && !allSelected;
                  const isCollapsed = collapsedGroups.has(group.group);

                  return (
                    <div key={group.group} className="border border-gray-200 rounded-xl overflow-hidden">
                      {/* Group header */}
                      <div
                        className="flex items-center px-4 py-2.5 bg-gray-50 cursor-pointer select-none hover:bg-gray-100 transition-colors"
                        onClick={() => toggleCollapseGroup(group.group)}
                      >
                        <button
                          type="button"
                          className="mr-2 text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded"
                          tabIndex={-1}
                        >
                          {isCollapsed
                            ? <ChevronRight className="w-4 h-4" />
                            : <ChevronDown className="w-4 h-4" />
                          }
                        </button>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = someSelected; }}
                          onChange={(e) => { e.stopPropagation(); toggleGroup(group); }}
                          className="w-4 h-4 rounded border-gray-300 text-red-600 focus-visible:ring-red-500 mr-3"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-sm font-semibold text-gray-800 flex-1">
                          {group.group}
                        </span>
                        <CountBadge count={selectedInGroup} theme="admin" size="sm" />
                      </div>

                      {/* Group fields */}
                      {!isCollapsed && (
                        <div className="px-4 py-2 grid grid-cols-2 gap-x-4 gap-y-1.5">
                          {group.fields.map((field) => {
                            const isSensitive = SENSITIVE_FIELD_IDS.has(field.id);
                            return (
                              <label
                                key={field.id}
                                className="flex items-center py-1 cursor-pointer group"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedFields.has(field.id)}
                                  onChange={() => toggleField(field.id)}
                                  className="w-4 h-4 rounded border-gray-300 text-red-600 focus-visible:ring-red-500 mr-2.5 flex-shrink-0"
                                />
                                <span className="text-sm text-gray-700 group-hover:text-gray-900 truncate">
                                  {field.label}
                                </span>
                                {isSensitive && (
                                  <span className="ml-1.5 px-1.5 py-0 rounded text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200 flex-shrink-0">
                                    PII
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button onClick={onClose} className="btn btn-admin-outline">
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={loading || selectedFields.size === 0}
              className="btn btn-admin-primary"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                  {selectedFields.size > 0 && (
                    <span className="ml-1.5 text-xs opacity-80">({selectedFields.size} fields)</span>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
